import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { RaciService } from '../../../raci/services/raci.service';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { Subscription } from 'rxjs';

/**
 * Handles the close valuation event dialog
 */
@Component({
  selector: 'close-valuation-event-dialog',
  templateUrl: './close-valuation-event-dialog.component.html',
  styleUrls: ['./close-valuation-event-dialog.component.css']
})
export class CloseValuationEventDialogComponent implements OnDestroy {

  /**
 * The active portfolio.
 */
  activePortfolio: any;

  /**
   * Setter for portfolio input property.
   */
  @Input() set portfolio(value: any) {
    if (value) {
      this.activePortfolio = value;
    }
  }

  /**
   * Indicates whether the portfolio can be closed.
   */
  @Input() canClose: boolean;

  /**
   * The message for the dialog.
   */
  @Input() dialogMessage: string;

  /**
   * The selected option.
   */
  @Input() optionSelected: number;

  /**
   * The selected copy properties option.
   */
  @Input() copyProperties: boolean;

  /**
   * The selected copy fx tables and currencies option.
   */
  @Input() copyFxTables: boolean;

  /**
   * The new valuation date
   */
  @Input() valuationDate: any;

  /**
   * The new valuation name
   */
  @Input() valuationName: string;

  /**
   * On close project event emitter
   */
  @Output() onCloseProject: EventEmitter<boolean> = new EventEmitter();

  /**
   * On create new valuation event emitter
   */
  @Output() onCreateValuationEvent: EventEmitter<boolean> = new EventEmitter();

  /**
   * The subscriptions
   */
  private _subscriptions: Array<Subscription> = new Array<Subscription>();
 
/**
  * Constructor
  * */
  constructor(private _raciService: RaciService,
    private _raciNavigationService: RaciNavigationService) { }

  /**
  * On destroy
  * */
  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * On cancel action event
   * */
  cancel() {
    this._raciNavigationService.showCloseValuationEventDialog(false);
  }

  /**
   * On continue action event
   * */
  continue() {
    switch (this.optionSelected) {
      case 1:
        this.closeValuationEvent();
        break;
      case 2:
        this.createValuationEvent();
        break;
      case 3:
        this.closeValuationEventProject();
        break;
      default:
        break;
    }
  }

  /**
   * On close valuation event 
   * */
  closeValuationEvent() {
    this._raciService.displayDataLoader(true);
    this.finalizePortfolio();
  }

  /**
   * Hides close valuation event dialog
   * */
  private hideCloseValuationDialog() {
    this._raciNavigationService.showCloseValuationEventDialog(false);
    this._raciService.displayDataLoader(false);
  }

  /**
   * Closes a portfolio
   * */
  private finalizePortfolio() {
    this._subscriptions.push(this._raciService.finalizePortfolio(this.activePortfolio.oid).subscribe(data => {
      if (data.success) {
        this.activePortfolio.isFinalized = true;
        this.hideCloseValuationDialog();
      } else {
        alert('Portfolio is already finalized');
      }
      this.hideCloseValuationDialog();
    }));
  }
    /**
   * Closes an opportunity
   * */
  private closeOpportunity() {
    this._subscriptions.push(this._raciService.closeOpportunity(this.activePortfolio.CfProjectOpportunityId, this.activePortfolio.projectId).subscribe(closedOpportunity => {
      if (!closedOpportunity.success) {
        this._raciService.displayDataLoader(false);
        alert("Project was not closed in PIX WF. \nThe closing message was not sent to CapForce, please contact technical support for details.");
      }
      else {
        alert("Project was successfully closed in PIX WF. \nThe closing message was successfully sent to CapForce.");
        this.subscribeToCloseProject();
      }
    }));
  }

  /**
  * Closes all valuation events and project
  * */
  closeValuationEventProject() {
    this._raciService.displayDataLoader(true);
    this.closeOpportunity();
  }

  /**
   * Subscribe to close project
   */
  subscribeToCloseProject() {
    this._subscriptions.push(this._raciService.closeProject(this.activePortfolio.projectId).subscribe(data => {
      if (data) {
        this.activePortfolio.isFinalized = true;
        this._raciService.displayDataLoader(false);
        this._raciNavigationService.setIsProjectClosed(true);
        this.onCloseProject.emit(true);
      }
    }));
  }

  /**
  * Closes selected valuation event and creates a new one
  * */
  createValuationEvent() {
    this._raciService.displayDataLoader(true);
    this.activePortfolio.valuationDate = this.valuationDate;
    this.activePortfolio.valuationPortfolioName = this.valuationName;
    this._subscriptions.push(this._raciService.createValuationEvent(this.activePortfolio, this.copyProperties, this.copyFxTables).subscribe(data => {
      if (data.success) {
        this.activePortfolio.isFinalized = true;
        this._raciNavigationService.showCloseValuationEventDialog(false);
        this.onCreateValuationEvent.emit(true);
      } else {
        alert('Error when creating new portfolio');
      }
    }));
  }
}
